import template from './sellops-rules.html.twig';

const { Component } = Shopware;
const { Criteria } = Shopware.Data;

function emptyRule() {
    return {
        id: null,
        name: '',
        // Empty array = applies to every sales channel. Same shape/semantics
        // as trigger_category_ids etc. — an id list with OR logic, enforced
        // server-side in UpsellRuleEntity::appliesToSalesChannel().
        sales_channel_ids: [],
        priority: 0,
        active: true,
        trigger_type: 'any',
        trigger_product_ids: [],
        trigger_category_ids: [],
        trigger_tags: [],
        trigger_product_stream_ids: [],
        offer_type: 'cross_selling',
        offer_product_ids: [],
        offer_tag_ids: [],
        offer_category_ids: [],
        offer_product_discounts: {},
        offer_product_stream_ids: [],
        discount_percent: 0,
        max_offers: 3,
    };
}

function emptySearch() {
    return { query: '', results: [], loading: false, open: false, labels: {} };
}

function emptySearchState() {
    return {
        triggerProducts:   emptySearch(),
        triggerCategories: emptySearch(),
        triggerTags:       emptySearch(),
        triggerStream:     emptySearch(),
        offerProducts:     emptySearch(),
        offerTags:         emptySearch(),
        offerCategories:   emptySearch(),
        offerStream:       emptySearch(),
        // Sales channels — same dropdown+chips picker UI as the fields
        // above, but backed by the already-loaded `salesChannels` list
        // (loadSalesChannels() below) instead of a live per-keystroke
        // repository search.
        salesChannels:     emptySearch(),
    };
}

function patchSearch(current, key, patch) {
    return { ...current, [key]: { ...current[key], ...patch } };
}

Component.register('sellops-rules', {
    template,

    inject: ['repositoryFactory', 'loginService', 'sellopsUpsellApiService'],

    mixins: [Shopware.Mixin.getByName('notification')],

    data() {
        return {
            isLoading: false,
            isSaving: false,
            rules: [],
            // Flat { id, name } list for the sales-channel picker and for
            // resolving a rule's sales_channel_ids to display names in the
            // rule list table (salesChannelsSummary() below). Loaded once
            // on created().
            salesChannels: [],
            showModal: false,
            ruleToDelete: null,
            editingRule: emptyRule(),
            search: emptySearchState(),
            validationErrors: {},
        };
    },

    computed: {
        apiContext() {
            return Shopware.Context.api;
        },

        categoryRepository() {
            return this.repositoryFactory.create('category');
        },

        tagRepository() {
            return this.repositoryFactory.create('tag');
        },

        maxDiscount() { return 100; },

    },

    created() {
        this._timers = {}; // non-reactive timer store (not in data())
        this.loadRules();
        this.loadSalesChannels();
    },

    methods: {

        _entityName(entityType) {
            return entityType === 'product-stream' ? 'product_stream' : entityType;
        },

        patchSearch(key, patch) {
            this.search = patchSearch(this.search, key, patch);
        },

        async loadRules() {
            this.isLoading = true;
            try {
                const data = await this.sellopsUpsellApiService.getRules();
                this.rules = Array.isArray(data) ? data : [];
            } catch{
                this.createNotificationError({ message: this.$tc('sellops.rules.errors.saveGeneric') });
            } finally {
                this.isLoading = false;
            }
        },

        /** Loads the full sales-channel list once for the picker dropdown
         *  and for salesChannelName()/salesChannelsSummary()'s lookups
         *  below. Failure is silent — worst case the picker shows no
         *  options and the list table shows raw IDs. */
        async loadSalesChannels() {
            try {
                const criteria = new Criteria(1, 500);
                criteria.addSorting(Criteria.sort('name', 'ASC'));
                const result = await this.repositoryFactory.create('sales_channel').search(criteria, Shopware.Context.api);
                this.salesChannels = result.map(sc => ({
                    id: sc.id,
                    name: sc.translated?.name || sc.name || sc.id,
                }));
            } catch {
                this.salesChannels = [];
            }
        },

        /** Display name for a single sales-channel id — used for the
         *  picker's chips in the modal. */
        salesChannelName(id) {
            const sc = this.salesChannels.find(s => s.id === id);
            return sc ? sc.name : id;
        },

        /** Summary text for a rule's sales_channel_ids — used in the rule
         *  list table badge. Empty/unset renders as "All sales channels";
         *  otherwise a comma-joined list of channel names. */
        salesChannelsSummary(rule) {
            const ids = rule.sales_channel_ids || [];
            if (!ids.length) return this.$tc('sellops.rules.salesChannelAll');
            return ids.map(id => this.salesChannelName(id)).join(', ');
        },

        /** Sales-channel picker dropdown — client-side filtering over the
         *  already-loaded channel list (no per-keystroke repository search
         *  needed, unlike the product/category/tag pickers above). */
        onFocusSalesChannels() {
            this.patchSearch('salesChannels', { open: true, results: this._filterSalesChannels('') });
        },

        onSalesChannelSearchInput(value) {
            this.patchSearch('salesChannels', { query: value, open: true, results: this._filterSalesChannels(value) });
        },

        _filterSalesChannels(query) {
            const q = (query || '').trim().toLowerCase();
            const list = q ? this.salesChannels.filter(sc => sc.name.toLowerCase().includes(q)) : this.salesChannels;
            return list.map(sc => ({ id: sc.id, name: sc.name }));
        },

        validateEditingRule() {
            const errors = {};
            const r = this.editingRule;

            const d = Number(r.discount_percent ?? 0);
            if (Number.isNaN(d) || d < 0) {
                errors.discount_percent = this.$tc('sellops.rules.errors.discountMin');
            } else if (d > this.maxDiscount) {
                errors.discount_percent = this.$tc('sellops.rules.errors.discountMax', 0, { max: this.maxDiscount });
            }

            const p = Number(r.priority ?? 0);
            if (!Number.isInteger(p) || p < 0) {
                errors.priority = this.$tc('sellops.rules.errors.priority');
            }

            const map = r.offer_product_discounts || {};
            for (const k of Object.keys(map)) {
                const v = map[k];
                if (v === '' || v === null || v === undefined) continue;
                const n = Number(v);
                if (Number.isNaN(n) || n < 0 || n > this.maxDiscount) {
                    errors.offer_product_discounts = this.$tc('sellops.rules.errors.perProduct', 0, { max: this.maxDiscount });
                    break;
                }
            }

            this.validationErrors = errors;
            return Object.keys(errors).length === 0;
        },

        async saveRule() {
            if (!this.validateEditingRule()) return;

            this.isSaving = true;
            const isNew = !this.editingRule.id;
            try {
                if (isNew) {
                    await this.sellopsUpsellApiService.createRule(this.editingRule);
                } else {
                    await this.sellopsUpsellApiService.updateRule(this.editingRule.id, this.editingRule);
                }
                this.closeModal();
                await this.loadRules();
            } catch (e) {
                if (e.response?.status === 409) {
                    const body = e.response.data || {};
                    if (body.field) {
                        this.validationErrors = { ...this.validationErrors, [body.field]: body.error || this.$tc('sellops.rules.errors.saveDuplicate') };
                    } else {
                        this.createNotificationError({
                            title:   this.$tc('sellops.rules.errors.saveErrorTitle'),
                            message: body.error || this.$tc('sellops.rules.errors.saveDuplicate'),
                        });
                    }
                } else {
                    this.createNotificationError({
                        title:   this.$tc('sellops.rules.errors.saveErrorTitle'),
                        message: this.$tc('sellops.rules.errors.saveGeneric'),
                    });
                }
            } finally {
                this.isSaving = false;
            }
        },

        deleteRule(rule) {
            this.ruleToDelete = rule;
        },

        cancelDeleteRule() {
            this.ruleToDelete = null;
        },

        async confirmDeleteRule() {
            const rule = this.ruleToDelete;
            this.ruleToDelete = null;
            try {
                await this.sellopsUpsellApiService.deleteRule(rule.id);
                this.rules = this.rules.filter(r => r.id !== rule.id);
                await this.loadRules();
            } catch{
                this.createNotificationError({ message: this.$tc('sellops.rules.errors.saveGeneric') });
            }
        },

        async toggleActive(rule) {
            rule.active = !rule.active;
            try {
                await this.sellopsUpsellApiService.updateRule(rule.id, { ...rule });
            } catch { /* ignore */ }
        },

        async moveUp(index) {
            if (index === 0) return;
            const arr = [...this.rules];
            [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
            this.rules = arr;
            await this.saveOrder();
        },

        async moveDown(index) {
            if (index === this.rules.length - 1) return;
            const arr = [...this.rules];
            [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
            this.rules = arr;
            await this.saveOrder();
        },

        async saveOrder() {
            try {
                await this.sellopsUpsellApiService.reorderRules(this.rules.map(r => r.id));
                this.rules.forEach((r, i) => { r.priority = i; });
            } catch { /* ignore */ }
        },

        openCreateModal() {
            this.editingRule = emptyRule();
            this.editingRule.priority = this.rules.length;
            this.search = emptySearchState();
            this.validationErrors = {};
            this.showModal = true;
        },

        openEditModal(rule) {
            this.editingRule = { ...rule };
            this.search = emptySearchState();
            this.validationErrors = {};
            this.showModal = true;

            const {
                trigger_product_ids,
                trigger_category_ids,
                trigger_tags,
                trigger_product_stream_ids,
                offer_product_ids,
                offer_product_stream_ids,
            } = this.editingRule;
            if (trigger_product_ids?.length)        this.loadLabels('triggerProducts',   'product',        trigger_product_ids);
            if (trigger_category_ids?.length)       this.loadLabels('triggerCategories', 'category',       trigger_category_ids);
            if (trigger_tags?.length)               this.loadLabels('triggerTags',       'tag',            trigger_tags);
            if (trigger_product_stream_ids?.length) this.loadLabels('triggerStream',     'product-stream', trigger_product_stream_ids);
            if (offer_product_ids?.length)          this.loadLabels('offerProducts',     'product',        offer_product_ids);
            if (this.editingRule.offer_tag_ids?.length)
                                                    this.loadLabels('offerTags',         'tag',            this.editingRule.offer_tag_ids);
            if (this.editingRule.offer_category_ids?.length)
                                                    this.loadLabels('offerCategories',   'category',       this.editingRule.offer_category_ids);
            if (offer_product_stream_ids?.length)   this.loadLabels('offerStream',       'product-stream', offer_product_stream_ids);

            // Sales-channel chip labels — resolved synchronously from the
            // already-loaded salesChannels list rather than an async
            // loadLabels() call.
            if (this.editingRule.sales_channel_ids?.length) {
                const labels = {};
                this.editingRule.sales_channel_ids.forEach((id) => {
                    labels[id] = this.salesChannelName(id);
                });
                this.patchSearch('salesChannels', { labels });
            }
        },

        closeModal() {
            this.showModal = false;
        },

        onFocus(key, entityType) {
            if (this.search[key].results.length) {
                this.patchSearch(key, { open: true });
                return;
            }
            if (this.search[key].loading) return;
            this.patchSearch(key, { open: true, loading: true });
            this.doSearch(key, entityType);
        },

        onBlur(key) {
            setTimeout(() => this.patchSearch(key, { open: false }), 220);
        },

        onSearchInput(key, entityType) {
            this.patchSearch(key, { open: true, loading: true });

            if (this._timers[key]) clearTimeout(this._timers[key]);
            this._timers[key] = setTimeout(() => this.doSearch(key, entityType), 280);
        },

        async doSearch(key, entityType) {
            const q          = (this.search[key].query || '').trim();
            const entityName = this._entityName(entityType);

            this.patchSearch(key, { loading: true, open: true });

            const criteria = new Criteria(1, 25);

            if (q) {
                if (entityType === 'product') {
                    criteria.addFilter(Criteria.multi('OR', [
                        Criteria.contains('name',          q),
                        Criteria.contains('productNumber', q),
                        Criteria.contains('ean',           q),
                    ]));
                } else {
                    criteria.addFilter(Criteria.contains('name', q));
                }
            }
            if (entityType === 'product') {
                criteria.addFilter(Criteria.equals('active', true));
            }

            try {
                const result  = await this.repositoryFactory.create(entityName).search(criteria, Shopware.Context.api);
                const results = result.map(item => ({
                    id:            item.id,
                    name:          item.translated?.name || item.name || item.id,
                    productNumber: item.productNumber || '',
                    ean:           item.ean || '',
                }));
                this.patchSearch(key, { loading: false, results });
            } catch{
                this.patchSearch(key, { loading: false, results: [] });
            }
        },

        async loadLabels(key, entityType, ids) {
            if (!ids || !ids.length) return;
            const entityName = this._entityName(entityType);
            const criteria   = new Criteria(1, ids.length + 10);
            criteria.addFilter(Criteria.equalsAny('id', ids));

            try {
                const result = await this.repositoryFactory.create(entityName).search(criteria, Shopware.Context.api);
                const labels = { ...this.search[key].labels };
                result.forEach(item => {
                    labels[item.id] = item.translated?.name || item.name || item.id;
                });
                this.patchSearch(key, { labels });
            } catch { /* ignore */ }
        },

        selectEntity(key, idsField, id, name) {
            const ids = this.editingRule[idsField] || [];
            if (!ids.includes(id)) {
                this.editingRule[idsField] = [...ids, id];
            }
            const labels = { ...this.search[key].labels, [id]: name };
            this.patchSearch(key, { query: '', open: true, labels });

        },

        selectSingleEntity(key, field, id, name) {
            this.editingRule[field] = id;
            const labels = { ...this.search[key].labels, [id]: name };
            this.patchSearch(key, { query: '', results: [], open: false, labels });
        },

        removeEntity(idsField, id) {
            this.editingRule[idsField] = (this.editingRule[idsField] || []).filter(i => i !== id);
        },

        _productKey(id) {
            return String(id || '').toLowerCase().replace(/-/g, '');
        },

        getPerProductDiscount(id) {
            const map = this.editingRule.offer_product_discounts || {};
            const key = this._productKey(id);
            const v   = map[key];
            return (v === undefined || v === null || v === '') ? '' : v;
        },

        setPerProductDiscount(id, value) {
            const key = this._productKey(id);
            const map = { ...(this.editingRule.offer_product_discounts || {}) };

            if (value === '' || value === null || value === undefined) {
                delete map[key];
            } else {
                const n = Number(value);
                map[key] = Number.isFinite(n) ? n : value;
            }
            this.editingRule.offer_product_discounts = map;
        },

        removePerProductDiscount(id) {
            const key = this._productKey(id);
            const map = { ...(this.editingRule.offer_product_discounts || {}) };
            delete map[key];
            this.editingRule.offer_product_discounts = map;
        },

        getPerProductDiscountPlaceholder() {
            if (this.editingRule.offer_type === 'manual') {
                return '0';
            }
            const d = Number(this.editingRule.discount_percent || 0);
            return d > 0 ? `${d}` : '0';
        },

        clearSingleEntity(field, key) {
            this.editingRule[field] = null;
            this.patchSearch(key, { labels: {}, query: '', results: [], open: false });
        },

        openDropdown(key) {
            this.patchSearch(key, { open: true });
        },

        closeDropdown(key) {
            setTimeout(() => this.patchSearch(key, { open: false }), 200);
        },

        getLabel(key, id) {
            return this.search[key]?.labels?.[id] || id;
        },

        triggerLabel(rule) {
            const cnt = (field) => (rule[field] || []).length;
            switch (rule.trigger_type) {
                case 'any':            return this.$tc('sellops.rules.chipTriggerAny');
                case 'products':       return this.$tc('sellops.rules.chipTriggerProducts',   0, { n: cnt('trigger_product_ids') });
                case 'categories':     return this.$tc('sellops.rules.chipTriggerCategories', 0, { n: cnt('trigger_category_ids') });
                case 'tags':           return this.$tc('sellops.rules.chipTriggerTags',       0, { n: cnt('trigger_tags') });
                case 'product_stream': return this.$tc('sellops.rules.chipTriggerStreams',    0, { n: cnt('trigger_product_stream_ids') });
                default:               return rule.trigger_type;
            }
        },

        offerLabel(rule) {
            const cnt = (field) => (rule[field] || []).length;
            switch (rule.offer_type) {
                case 'cross_selling':              return this.$tc('sellops.rules.chipOfferCrossSelling');
                case 'frequently_bought_together': return this.$tc('sellops.rules.chipOfferFbt');
                case 'category_top_seller':        return this.$tc('sellops.rules.chipOfferCategoryTop');
                case 'manual':                     return this.$tc('sellops.rules.chipOfferManual',     0, { n: cnt('offer_product_ids') });
                case 'tags':                       return this.$tc('sellops.rules.chipOfferTags',       0, { n: cnt('offer_tag_ids') });
                case 'categories':                 return this.$tc('sellops.rules.chipOfferCategories', 0, { n: cnt('offer_category_ids') });
                case 'product_stream':             return this.$tc('sellops.rules.chipOfferStreams',    0, { n: cnt('offer_product_stream_ids') });
                default:                           return rule.offer_type;
            }
        },
    },
});
